import React, { useEffect, useRef, useState } from 'react';
import { X, Search, LocateFixed, MapPin, Check } from 'lucide-react';
import { loadGoogleMaps, extractCity, extractStateAndPincode } from '@/lib/googleMaps';
import { Button } from '@/components/ui/Button';

// Swiggy/Zomato-style location picker: search-as-you-type address
// autocomplete, a "use my current location" shortcut, and a small map with
// a draggable pin so the exact spot can be fine-tuned before confirming.
// Used from: AdminVenues (tagging a venue's address), the customer Venues
// page and Landing page (choosing where to search "nearby" from).
//
// Renders nothing (returns null) until `open` is true, so mounting it
// ahead of time is cheap — the Google Maps script only loads on first open.
//
// onConfirm receives { address, city, state, pincode, latitude, longitude, place_id }.
export function LocationPicker({ open, onClose, onConfirm, title = 'Select a location' }) {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [locating, setLocating] = useState(false);
  const [picked, setPicked] = useState(null); // { address, city, latitude, longitude, place_id }

  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStatus('loading');
    loadGoogleMaps()
      .then((google) => {
        if (cancelled) return;
        geocoderRef.current = new google.maps.Geocoder();
        if (inputRef.current) {
          autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
            fields: ['formatted_address', 'geometry', 'address_components', 'place_id'],
          });
          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();
            if (!place?.geometry?.location) return;
            applyLocation({
              address: place.formatted_address || '',
              city: extractCity(place.address_components),
              ...extractStateAndPincode(place.address_components),
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
              place_id: place.place_id || null,
            });
          });
        }
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ensureMap = (google, lat, lng) => {
    if (!mapRef.current) return;
    if (!mapObjRef.current) {
      mapObjRef.current = new google.maps.Map(mapRef.current, {
        center: { lat, lng },
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
      });
      markerRef.current = new google.maps.Marker({
        position: { lat, lng },
        map: mapObjRef.current,
        draggable: true,
      });
      markerRef.current.addListener('dragend', () => {
        const pos = markerRef.current.getPosition();
        reverseGeocode(pos.lat(), pos.lng());
      });
    } else {
      mapObjRef.current.setCenter({ lat, lng });
      markerRef.current.setPosition({ lat, lng });
    }
  };

  const applyLocation = (loc) => {
    setPicked(loc);
    if (window.google) ensureMap(window.google, loc.latitude, loc.longitude);
  };

  const reverseGeocode = (lat, lng) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ location: { lat, lng } }, (results, geoStatus) => {
      const best = geoStatus === 'OK' && results?.[0];
      applyLocation({
        address: best?.formatted_address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        city: best ? extractCity(best.address_components) : '',
        ...(best ? extractStateAndPincode(best.address_components) : { state: '', pincode: '' }),
        latitude: lat,
        longitude: lng,
        place_id: best?.place_id || null,
      });
    });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        reverseGeocode(pos.coords.latitude, pos.coords.longitude);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="location-picker-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-bold text-2xl uppercase">{title}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>

        {status === 'error' ? (
          <p className="text-sm text-white/60">
            Location search isn't available right now. Check your connection and try again in a moment.
          </p>
        ) : (
          <>
            <div className="relative mb-3">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                ref={inputRef}
                type="text"
                placeholder={status === 'loading' ? 'Loading…' : 'Search for an address or area'}
                disabled={status === 'loading'}
                className="w-full h-11 pl-10 pr-4 bg-transparent border border-white/20 text-white placeholder:text-white/30 focus:border-volt focus:outline-none disabled:opacity-50"
                data-testid="location-search-input"
              />
            </div>

            <button
              type="button"
              onClick={useMyLocation}
              disabled={status !== 'ready' || locating}
              className="mb-4 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-volt hover:text-volt-hover disabled:opacity-50"
              data-testid="location-use-current"
            >
              <LocateFixed size={12} /> {locating ? 'Locating…' : 'Use my current location'}
            </button>

            {picked && (
              <div className="mb-4">
                <div ref={mapRef} className="w-full h-48 bg-obsidian border border-white/10 mb-3" />
                <div className="flex items-start gap-2 text-sm text-white/80 bg-obsidian border border-white/10 p-3">
                  <MapPin size={14} className="mt-0.5 text-volt shrink-0" />
                  <span>{picked.address}</span>
                </div>
                <p className="mt-2 text-[11px] text-white/40">Drag the pin on the map to fine-tune the exact spot.</p>
              </div>
            )}

            <div className="flex gap-3 mt-2">
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button
                onClick={() => picked && onConfirm(picked)}
                disabled={!picked}
                className="flex-1"
                data-testid="location-confirm"
              >
                <Check size={14} /> Confirm Location
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
